'use client';

import { useRef, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Search, Loader2, ArrowRight, CheckCircle2, MapPin, PartyPopper, Eye, BadgeCheck, CalendarCheck } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import LocationPicker, { type LocationPickerHandle, type LocationValue, type DirectoryPin } from '@/components/landing/LocationPicker';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import type { LandingColors } from '@/constants/landing-content/types';
import { API_BASE } from '@/lib/env';

const DIRECTORY_API_BASE = process.env.NODE_ENV === 'development' ? '/api/local' : API_BASE;

// Featured businesses (gold pins) — real listings already in our directory.
const FEATURED_SLUGS = new Set([
  'boxinbox-music-chamartin',
  'tablada-25-tetuan',
  'pink-river-music-arganzuela',
]);

const BENEFITS = [
  { icon: Eye, text: 'Muestra tu teléfono y web a quien te busca' },
  { icon: BadgeCheck, text: 'Aparece como verificado y genera confianza' },
  { icon: CalendarCheck, text: 'Recibe reservas y mensajes de músicos' },
];

// Real directory size for social proof (rounded down; ~395 today).
const DIRECTORY_COUNT = 390;

interface ClaimSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  colors: LandingColors;
  initialType?: 'school' | 'rehearsal';
  returnFocusRef?: { current: HTMLElement | null };
}

type SearchResult = {
  id: string;
  slug: string;
  name: string;
  city: string;
  type: string;
  is_claimed: boolean;
};

const TYPE_LABELS: Record<string, string> = {
  school: 'Escuela de Música',
  rehearsal: 'Local de Ensayo',
  store: 'Tienda de Música',
  club: 'Club de Música',
};

const TYPE_OPTIONS = [
  { value: 'school', label: 'Escuela de música' },
  { value: 'rehearsal', label: 'Sala / local de ensayo' },
  { value: 'store', label: 'Tienda de música' },
  { value: 'club', label: 'Club / sala de conciertos' },
];

export default function ClaimSearchModal({ open, onOpenChange, colors, initialType = 'school', returnFocusRef }: ClaimSearchModalProps) {
  const router = useRouter();
  const { executeCaptcha } = useRecaptcha();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [pins, setPins] = useState<DirectoryPin[]>([]);

  // "Add my business" fallback (shown when search returns nothing, or forced
  // from the "none of these is mine" safety-net button — so we don't lose the lead).
  const [addForm, setAddForm] = useState({ name: '', city: '', address: '', type: initialType as string, email: '', phone: '', website: '' });
  const [coords, setCoords] = useState<LocationValue | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [addDone, setAddDone] = useState(false);
  const [addError, setAddError] = useState('');
  const [forcedAdd, setForcedAdd] = useState(false);
  const mapRef = useRef<LocationPickerHandle>(null);

  const showAddForm = !addDone && ((results !== null && results.length === 0) || forcedAdd);

  const forceAddForm = () => {
    setForcedAdd(true);
    setAddForm((f) => ({ ...f, name: f.name || query.trim() }));
  };

  // Load directory pins for the map when the modal opens (once).
  useEffect(() => {
    if (!open || pins.length) return;
    fetch(`${DIRECTORY_API_BASE}/directory?city=Madrid&limit=40`)
      .then((r) => {
        if (!r.ok) throw new Error(`Directory API error: ${r.status}`);
        return r.json();
      })
      .then((d) => {
        const list: DirectoryPin[] = (d.listings || [])
          .filter((l: { latitude?: number; longitude?: number }) => l.latitude && l.longitude)
          .map((l: { slug: string; name: string; type: string; city: string; is_claimed?: boolean; latitude: number; longitude: number }) => ({
            slug: l.slug,
            name: l.name,
            type: l.type,
            city: l.city,
            is_claimed: l.is_claimed,
            latitude: l.latitude,
            longitude: l.longitude,
            featured: FEATURED_SLUGS.has(l.slug),
          }));
        setPins(list);
      })
      .catch(() => {});
  }, [open, pins.length]);

  // Live autocomplete (debounced) — search as the owner types.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) return;
    const controller = new AbortController();
    const t = setTimeout(async () => {
      setSearching(true);
      setSearchError('');
      setForcedAdd(false);
      try {
        const res = await fetch(`${DIRECTORY_API_BASE}/directory?q=${encodeURIComponent(q)}&limit=8`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Directory API error: ${res.status}`);
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        setResults(data.listings || []);
        setAddForm((f) => ({ ...f, name: f.name || q }));
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setResults(null);
        setSearchError('No hemos podido buscar ahora mismo. Inténtalo de nuevo.');
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, 350);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [query]);

  const goToPin = (p: DirectoryPin) => {
    onOpenChange(false);
    // Unclaimed → open the listing with the claim dialog; claimed → just view it.
    router.push(`/directorio/${p.slug}${p.is_claimed ? '' : '?claim=1'}`);
  };

  const goToListing = (r: SearchResult) => {
    onOpenChange(false);
    router.push(`/directorio/${r.slug}${r.is_claimed ? '' : '?claim=1'}`);
  };

  // Geocode from the typed city + address so the marker lands roughly right.
  const locateOnMap = () => {
    const q = [addForm.address, addForm.city].filter(Boolean).join(', ');
    if (q.trim().length > 2) mapRef.current?.locate(q);
  };

  const submitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.name.trim() || !addForm.city.trim() || !addForm.email.trim()) return;
    setSubmitting(true);
    setAddError('');
    try {
      const captcha = await executeCaptcha('submit_request');
      const res = await fetch(`${API_BASE}/directory/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...addForm,
          latitude: coords?.latitude,
          longitude: coords?.longitude,
          captcha_token: captcha.token,
          captcha_provider: captcha.provider,
          recaptcha_token: captcha.provider === 'recaptcha' ? captcha.token : undefined,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success !== true || !d.id) {
        throw new Error('No se pudo confirmar el guardado. Inténtalo de nuevo.');
      }
      setAddDone(true);
    } catch (err) {
      setAddError(err instanceof Error && err.message ? err.message : 'No se pudo enviar. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onCloseAutoFocus={returnFocusRef ? e => { e.preventDefault(); returnFocusRef.current?.focus(); } : undefined} className="font-landing !w-[95vw] !max-w-[64rem] !max-h-[92vh] !gap-0 overflow-hidden !p-0">
        <div className="flex max-h-[92vh] flex-col md:grid md:min-h-[600px] md:grid-cols-[1.1fr_1fr] md:grid-rows-1">
          {/* MAP — top on mobile, right column on desktop. Single instance. */}
          <div className="relative h-[240px] w-full shrink-0 bg-gray-50 md:col-start-2 md:row-start-1 md:h-auto">
            <LocationPicker
              ref={mapRef}
              colors={colors}
              onChange={setCoords}
              fill
              pins={pins}
              showMarker={showAddForm}
              onPinClick={showAddForm ? undefined : goToPin}
            />
          </div>

          {/* FORM — below map on mobile, left column on desktop */}
          <div className="flex-1 overflow-y-auto p-6 md:col-start-1 md:row-start-1 md:max-h-[92vh] md:p-8">
            <DialogHeader>
              <DialogTitle className="font-landing text-2xl font-bold tracking-tight text-gray-900">
                Encuentra tu negocio
              </DialogTitle>
              <DialogDescription className="text-base text-gray-600">
                Búscalo por nombre o ciudad y recláma­lo en un minuto. Es gratis.
              </DialogDescription>
            </DialogHeader>

            <div className="relative mt-4">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => {
                  const nextQuery = e.target.value;
                  setQuery(nextQuery);
                  setSearchError('');
                  if (nextQuery.trim().length < 2) {
                    setResults(null);
                    setAddDone(false);
                    setForcedAdd(false);
                  }
                }}
                placeholder="Nombre de tu negocio o ciudad"
                aria-label="Busca tu negocio"
                className="h-12 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-10 text-base text-gray-900 outline-none focus:border-gray-400"
              />
              {searching && <Loader2 className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-gray-400" />}
            </div>
            {searchError && (
              <p role="alert" className="mt-2 text-sm text-red-600">{searchError}</p>
            )}

            {/* Benefits — shown while the searcher hasn't typed yet */}
            {results === null && !addDone && (
              <>
                <ul className="mt-6 space-y-3">
                  {BENEFITS.map((b, i) => {
                    const Icon = b.icon;
                    return (
                      <li key={i} className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${colors.primary}10` }}>
                          <Icon className="h-4 w-4" style={{ color: colors.primary }} />
                        </span>
                        <span className="text-sm text-gray-600">{b.text}</span>
                      </li>
                    );
                  })}
                </ul>

                {/* Social proof + honest urgency */}
                <div className="mt-5 rounded-xl border border-gray-100 bg-gray-50 p-3">
                  <p className="text-sm font-semibold text-gray-900">
                    +{DIRECTORY_COUNT} negocios de música ya están en Jamsom Music
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Tu competencia ya aparece. Asegúrate de que a ti también te encuentren.
                  </p>
                </div>
                <p className="mt-3 text-xs text-gray-400">Gratis para siempre · Verificas que es tuyo en 2 minutos</p>
              </>
            )}

            {/* Results found */}
            {results !== null && results.length > 0 && !forcedAdd && (
              <ul className="mt-4 space-y-2">
                {results.map((r) => (
                  <li key={r.id}>
                    <button
                      onClick={() => goToListing(r)}
                      className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-gray-300"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-gray-900">{r.name}</p>
                        <p className="flex items-center gap-1 truncate text-sm text-gray-500">
                          <MapPin className="h-3.5 w-3.5" /> {TYPE_LABELS[r.type] || r.type} · {r.city}
                        </p>
                      </div>
                      {r.is_claimed ? (
                        <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Ya reclamada</span>
                      ) : (
                        <span className="flex shrink-0 items-center gap-1 text-sm font-bold" style={{ color: colors.primary }}>Reclamar <ArrowRight className="h-4 w-4" /></span>
                      )}
                    </button>
                  </li>
                ))}
                {/* Safety net: none of these is mine → add my own (captures the lead) */}
                <li>
                  <button
                    onClick={forceAddForm}
                    className="mt-1 w-full rounded-xl border border-dashed border-gray-300 p-3 text-center text-sm font-medium text-gray-600 transition-colors hover:border-gray-400 hover:text-gray-800"
                  >
                    ¿No es ninguno de estos? <span style={{ color: colors.primary }}>Da de alta tu negocio →</span>
                  </button>
                </li>
              </ul>
            )}

            {/* Success */}
            {addDone && (
              <div className="mt-4 flex flex-col items-center rounded-xl border border-gray-200 bg-gray-50 p-6 text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full" style={{ backgroundColor: `${colors.primary}15` }}>
                  <PartyPopper className="h-6 w-6" style={{ color: colors.primary }} />
                </div>
                <p className="font-landing text-lg font-bold text-gray-900">Casi listo: revisa tu email</p>
                <p className="mt-1 text-sm text-gray-600">
                  Te hemos enviado un email a <span className="font-semibold text-gray-800">{addForm.email}</span> para confirmar
                  que el negocio es tuyo. Pulsa el enlace y lo publicamos en menos de 24 h.
                </p>
              </div>
            )}

            {/* Add-my-business form (map lives in the right column) */}
            {showAddForm && (
              <form onSubmit={submitAdd} className="mt-4 space-y-2.5">
                <p className="text-sm text-gray-600">
                  No encontramos tu negocio. <span className="font-semibold text-gray-800">Dalo de alta gratis</span> y lo
                  añadimos al directorio (te pediremos confirmar tu email y lo revisamos antes de publicar).
                </p>
                <input
                  type="text" required placeholder="Nombre de tu negocio *"
                  value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                  className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none focus:border-gray-400"
                />
                <div className="grid grid-cols-2 gap-2.5">
                  <input
                    type="text" required placeholder="Ciudad *"
                    value={addForm.city} onChange={(e) => setAddForm({ ...addForm, city: e.target.value })}
                    onBlur={locateOnMap}
                    className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none focus:border-gray-400"
                  />
                  <select
                    value={addForm.type} onChange={(e) => setAddForm({ ...addForm, type: e.target.value })}
                    aria-label="Tipo de negocio"
                    className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none focus:border-gray-400"
                  >
                    {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <input
                  type="text" placeholder="Dirección (para ubicarte en el mapa) →"
                  value={addForm.address} onChange={(e) => setAddForm({ ...addForm, address: e.target.value })}
                  onBlur={locateOnMap}
                  className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none focus:border-gray-400"
                />
                <div className="grid grid-cols-2 gap-2.5">
                  <input
                    type="email" required placeholder="Email del negocio *"
                    value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                    className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none focus:border-gray-400"
                  />
                  <input
                    type="tel" placeholder="Teléfono"
                    value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
                    className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none focus:border-gray-400"
                  />
                </div>
                <input
                  type="text" placeholder="Web o Instagram (opcional)"
                  value={addForm.website} onChange={(e) => setAddForm({ ...addForm, website: e.target.value })}
                  className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none focus:border-gray-400"
                />
                {addError && <p className="text-xs text-red-600">{addError}</p>}
                <button
                  type="submit" disabled={submitting}
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-lg text-sm font-semibold text-white shadow-sm transition-transform hover:scale-[1.01] disabled:opacity-50"
                  style={{ backgroundColor: colors.primary }}
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Dar de alta mi negocio
                </button>
                <p className="text-center text-[11px] text-gray-400">
                  Te enviaremos un email para confirmar · Lo revisamos antes de publicar · Gratis
                </p>
              </form>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
